import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { catchError, distinctUntilChanged, EMPTY, finalize, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { Adherent } from 'src/app/shared/interfaces/BDD-Ref/adherent';
import { AccordExtractionSharePointService } from 'src/app/shared/services/accord-extraction-sharepoint.service';
import { SnackbarHelper } from 'src/app/shared/services/snackbar/snackbar.helper';
import { SnackbarService } from 'src/app/shared/services/snackbar/snackbar.service';
import { UserSessionService } from 'src/app/shared/services/user.service';

@Component({
  selector: 'app-accord-extraction',
  standalone: true,
  imports: [CommonModule, MatIcon, MatIconModule, MatIconButton],

  templateUrl: './accord-extraction.component.html',
  styleUrl: './accord-extraction.component.scss',
})
export class AccordExtractionComponent implements OnInit, OnDestroy {
  private readonly _destroying$: Subject<void> = new Subject<void>();
  adherent!: Adherent;
  selectedFile: File | null = null;
  uploading = false;
  isDragging = false;

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  ngOnInit(): void {
    this._userService
      .getSelectedAdherent()
      .pipe(
        distinctUntilChanged((a, b) => (a?.idAdherent ?? null) === (b?.idAdherent ?? null)),
        takeUntil(this._destroying$),
        tap((adherent: Adherent | null) => {
          if (adherent) {
            this.setAdherent(adherent);
          }
        }),
      )
      .subscribe();
  }
  constructor(
    private readonly _userService: UserSessionService,
    private readonly _snackBarService: SnackbarService,
    private readonly cdr: ChangeDetectorRef,
    private readonly _accordExtractionSharePointService: AccordExtractionSharePointService,
  ) {}

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }
  private setAdherent(adherent: Adherent): void {
    this.adherent = adherent;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files || input.files.length === 0) {
      return;
    }

    this.selectedFile = input.files[0];
  }

  /**
   * Assigns the selected file for upload.
   *
   * This method is used by both file input selection and drag & drop.
   *
   * @param file Selected file
   */
  setSelectedFile(file: File): void {
    this.selectedFile = file;
  }

  /**
   * Handles the drag over event on the upload zone.
   *
   * This method:
   * - Prevents default browser behavior (file opening)
   * - Stops event propagation
   * - Activates visual feedback for drag state
   *
   * @param event DragEvent triggered when a file is dragged over the drop zone
   */
  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    this.isDragging = true;
  }

  /**
   * Handles the drag leave event when the dragged file leaves the drop zone.
   *
   * This method:
   * - Prevents default browser behavior
   * - Stops event propagation
   * - Removes drag visual feedback
   *
   * @param event DragEvent triggered when the cursor leaves the drop zone
   */
  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    this.isDragging = false;
  }

  /**
   * Handles the drop event when a file is dropped into the upload zone.
   *
   * This method:
   * - Prevents default browser file opening behavior
   * - Stops event propagation
   * - Extracts the dropped file
   * - Assigns the file as the selected file for upload
   * - Resets drag visual feedback
   *
   * Only the first file is processed (single-file upload mode).
   *
   * @param event DragEvent triggered when a file is dropped into the drop zone
   */
  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    this.isDragging = false;

    const files = event.dataTransfer?.files;

    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];

    this.setSelectedFile(file);
  }

  openFileDialog(): void {
    this.fileInput.nativeElement.click();
  }

  /**
   * Uploads the selected file to SharePoint with a business-specific naming convention.
   *
   * This method performs the following steps:
   * - Validates that a file has been selected
   * - Validates that required adherent business data is available
   * - Sends the file to the backend SharePoint API
   * - Displays success or error notifications to the user
   * - Manages upload loading state
   *
   * Required adherent fields:
   * - filiale
   * - activite
   * - ville
   * - nom
   *
   * UI Behavior:
   * - Shows error snackbar if validation fails
   * - Shows success snackbar on successful upload
   * - Disables upload button while uploading
   * - Resets selected file after success
   *
   * @returns void
   */
  uploadFile(): void {
    if (!this.selectedFile) {
      this._snackBarService.show('Veuillez sélectionner un fichier', 'snackbar-error');
      return;
    }

    if (!this.adherent?.filiale) {
      this._snackBarService.show('Filiale introuvable', 'snackbar-error');
      return;
    }
    if (
      !this.adherent.filiale ||
      !this.adherent.activite ||
      !this.adherent.ville ||
      !this.adherent.nom
    ) {
      this._snackBarService.show(
        'Informations officine incomplètes',
        SnackbarHelper.snackbarPanelClassError(),
      );
      return;
    }

    this.uploading = true;

    this._accordExtractionSharePointService
      .uploadFile(
        this.selectedFile,
        this.adherent.filiale,
        this.adherent.activite,
        this.adherent.ville,
        this.adherent.nom,
      )
      .pipe(
        takeUntil(this._destroying$),
        catchError(err => {
          // Erreur lors de l'upload vers SharePoint
          this._snackBarService.show(
            "Erreur lors de l'envoi du fichier : " + (err?.error || 'Erreur inconnue'),
            SnackbarHelper.snackbarPanelClassError(),
          );
          return EMPTY;
        }),
        switchMap(result => {
          const link = result?.url;

          if (!link || typeof link !== 'string' || link.trim() === '') {
            this._snackBarService.show(
              'URL du fichier invalide ou manquante',
              SnackbarHelper.snackbarPanelClassError(),
            );
            return EMPTY;
          }

          return this._accordExtractionSharePointService
            .sendMailAfterUpload(this.adherent.filiale, this.adherent.nom!, link)
            .pipe(
              catchError(err => {
                // Erreur lors de l'envoi du mail (upload réussi)
                this._snackBarService.show(
                  "Erreur lors de l'envoi du mail : " + (err?.error || 'Erreur inconnue'),
                  SnackbarHelper.snackbarPanelClassError(),
                );
                return EMPTY;
              }),
            );
        }),
        tap(() => {
          // Succès complet : upload + mail envoyés
          this._snackBarService.show(
            'Fichier envoyé et mail envoyé avec succès',
            SnackbarHelper.snackbarPanelClassSuccess(),
          );
          this.selectedFile = null;
        }),
        finalize(() => {
          this.uploading = false;
        }),
      )
      .subscribe();
  }

  /**
   * Downloads the SharePoint template file associated with the current adherent filiale.
   *
   * This method performs the following steps:
   * - Validates that the adherent filiale exists
   * - Calls the backend SharePoint API to retrieve the template file
   * - Extracts the filename from the HTTP Content-Disposition header (UTF-8 compatible)
   * - Creates a temporary download link in the browser
   * - Triggers the file download
   * - Displays success or error notifications to the user
   *
   * Technical details:
   * - Supports RFC 5987 filename encoding (filename*=UTF-8'')
   * - Uses Blob URL for browser-safe downloads
   * - Automatically revokes object URL to prevent memory leaks
   *
   * UI Behavior:
   * - Shows error snackbar if filiale is missing
   * - Shows success snackbar when download completes
   * - Shows error snackbar if the download fails
   *
   * @returns void
   */
  downloadTemplate(): void {
    if (!this.adherent?.filiale) {
      this._snackBarService.show('Filiale introuvable', SnackbarHelper.snackbarPanelClassError());
      return;
    }

    this._accordExtractionSharePointService
      .downloadTemplate(this.adherent.filiale)
      .pipe(takeUntil(this._destroying$))
      .subscribe({
        next: response => {
          const blob = response.body!;

          // Récupération du nom fichier depuis header
          const contentDisposition = response.headers.get('content-disposition');

          let fileName = 'download';

          if (contentDisposition) {
            // Priorité UTF-8 (filename*)
            const utf8Match = contentDisposition.match(/filename\*\=UTF-8''(.+)/);

            if (utf8Match && utf8Match[1]) {
              fileName = decodeURIComponent(utf8Match[1]);
            } else {
              // Fallback filename classique
              const asciiMatch = contentDisposition.match(/filename="?([^"]+)"?/);

              if (asciiMatch && asciiMatch[1]) {
                fileName = asciiMatch[1];
              }
            }
          }

          const url = window.URL.createObjectURL(blob);

          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          a.click();

          window.URL.revokeObjectURL(url);

          this._snackBarService.show(
            'Téléchargement du modèle réussi',
            SnackbarHelper.snackbarPanelClassSuccess(),
          );
        },
        error: err => {
          if (err.error instanceof Blob) {
            const reader = new FileReader();

            reader.onload = () => {
              const errorMessage = reader.result as string;

              this._snackBarService.show(
                'Erreur lors du téléchargement du modèle : ' + errorMessage,
                SnackbarHelper.snackbarPanelClassError(),
              );
            };

            reader.readAsText(err.error);
          } else {
            this._snackBarService.show(
              'Erreur lors du téléchargement du modèle',
              SnackbarHelper.snackbarPanelClassError(),
            );
          }
        },
      });
  }
}
